import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "antd";
import {
  CustomerServiceOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import paths from "../../routes/paths";
import { brandName } from "../../pages/care/careData";
import WorkspacePicker from "../../workspaces/WorkspacePicker";
import { OLIVEEB_COLORS } from "../../workspaces/colors";
import "../layout/layout.css";

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");

const userDisplayName = (user) =>
  cleanText(user?.displayName) ||
  [user?.preferredFirstName || user?.firstName, user?.preferredLastName || user?.lastName]
    .map(cleanText)
    .filter(Boolean)
    .join(" ") ||
  cleanText(user?.name) ||
  cleanText(user?.username) ||
  cleanText(user?.email) ||
  "Care Provider";

const userRoleLabel = (user) =>
  cleanText(user?.roleLabel) ||
  cleanText(user?.profession) ||
  cleanText(user?.jobTitle) ||
  cleanText(user?.title) ||
  cleanText(user?.primaryRole) ||
  cleanText(user?.role) ||
  "Care Provider";

const userInitials = (name) => {
  const parts = cleanText(name).split(/\s+/).filter(Boolean);
  if (!parts.length) return "CP";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
};

export function Logo() {
  return (
    <Link className="brand" to="/">
      <span className="brand-mark"><i className="ti ti-heart-handshake" /></span>
      <span>{brandName}</span>
    </Link>
  );
}

export function HeaderUserProfile() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [ddOpen, setDdOpen] = useState(false);
  const menuRef = useRef(null);
  const name = userDisplayName(user);
  const role = userRoleLabel(user);
  const photo = cleanText(user?.profilePhoto || user?.avatarUrl || user?.photoUrl || user?.imageUrl);
  const email = cleanText(user?.email) || "Oliveeb account";
  const initials = useMemo(() => userInitials(name), [name]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuRef.current || menuRef.current.contains(event.target)) return;
      setDdOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const profileMenuItems = [
    {
      label: "Profile",
      description: "View your Oliveeb identity",
      icon: <UserOutlined />,
      tone: "blue",
      path: paths.profile,
    },
    {
      label: "Settings",
      description: "Tune your workspace experience",
      icon: <SettingOutlined />,
      tone: "yellow",
      path: paths.settings,
    },
    {
      label: "Help",
      description: "Guides, answers, and tips",
      icon: <QuestionCircleOutlined />,
      tone: "blue",
      path: paths.help,
    },
    {
      label: "Support",
      description: "Get help from Oliveeb",
      icon: <CustomerServiceOutlined />,
      tone: "orange",
      path: paths.support,
    },
  ];

  const goToProfileMenuItem = (path) => {
    setDdOpen(false);
    navigate(path);
  };

  return (
    <div className="profile-tools">
      <span className="notification"><i className="ti ti-bell" /><b>3</b></span>
      <div
        className="care-profile-trigger pm-avw"
        ref={menuRef}
        role="button"
        tabIndex={0}
        aria-label="Open profile menu"
        aria-expanded={ddOpen}
        onClick={(event) => {
          event.stopPropagation();
          setDdOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          setDdOpen((current) => !current);
        }}
      >
        <div className="pm-av care-profile-avatar">
          <Avatar
            src={photo || undefined}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "999px",
              background: photo
                ? undefined
                : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebDarkBlue}, ${OLIVEEB_COLORS.oliveebBlue})`,
              color: OLIVEEB_COLORS.white,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {!photo ? initials : null}
          </Avatar>
          <span className="pm-avon" />
        </div>
        <div className="profile-label"><strong>{name}</strong><span>{role}</span></div>
        <i className={`ti ti-chevron-down care-profile-chevron${ddOpen ? " is-open" : ""}`} />

        {ddOpen && (
          <div className="pm-dd" onClick={(event) => event.stopPropagation()}>
            <div className="pm-ddh">
              <div className="pm-dd-avatar">
                <Avatar
                  src={photo || undefined}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "999px",
                    background: photo
                      ? undefined
                      : `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen}, ${OLIVEEB_COLORS.oliveebBlue})`,
                    color: OLIVEEB_COLORS.white,
                    fontSize: 16,
                    fontWeight: 900,
                  }}
                >
                  {!photo ? initials : null}
                </Avatar>
              </div>
              <div className="pm-dd-user">
                <div className="pm-dd-kicker">Signed in as</div>
                <div className="pm-ddn">{name}</div>
                <div className="pm-dde">{email}</div>
              </div>
            </div>
            <div className="pm-dd-list">
              {profileMenuItems.map((item) => (
                <div
                  key={item.label}
                  className="pm-ddi pm-dd-action"
                  data-tone={item.tone}
                  onClick={() => goToProfileMenuItem(item.path)}
                >
                  <span className="pm-dd-ico">{item.icon}</span>
                  <span className="pm-dd-copy">
                    <span className="pm-dd-label">{item.label}</span>
                    <span className="pm-dd-desc">{item.description}</span>
                  </span>
                </div>
              ))}
            </div>
            <div
              className="pm-ddi pm-dd-action dng mt-2"
              onClick={() => logout(false, paths.landing)}
            >
              <span className="pm-dd-ico"><LogoutOutlined /></span>
              <span className="pm-dd-copy">
                <span className="pm-dd-label">Sign out</span>
                <span className="pm-dd-desc">End this browser session</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const generalMenuItems = [
  ["Workspaces", "ti-layout-grid", "/workspaces"],
  ["Organizations", "ti-building", "/organizations"],
  ["Invitations", "ti-mail-opened", "/invitations"],
  ["Website", "ti-world", "/website"],
  ["Security & Access", "ti-shield-lock", "/access-control"],
  ["Settings", "ti-settings", "/settings"],
  ["Chat", "ti-messages", "/chat-space"],
  ["Inbox", "ti-inbox", "/email-messages"],
  ["Help & Support", "ti-headset", "/support"],
  ["Share Feedback", "ti-message-report", "/feedback"],
  ["Legal and Privacy", "ti-file-certificate", "/privacy-policy"],
];

export function SideNav({ collapsed = false }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const location = useLocation();
  const { selectedWorkspaceId } = useWorkspace();
  const workspaceId = selectedWorkspaceId || "current";
  const hireCareGiverMenuItems = [
    ["Talent Search", "ti-search", "/hire-care-giver/talent-search"],
    ["Job Posts", "ti-file-text", "/hire-care-giver/job-posts"],
    ["Applications", "ti-clipboard-list", "/hire-care-giver/applications"],
    ["Interviews & Hirings", "ti-calendar-check", "/hire-care-giver/interviews"],
    ["Create Job", "ti-plus", "/hire-care-giver/job-posts/new"],
    ["Professional Profiles", "ti-id", "/hire-care-giver/professional-profiles"],
    ["Background Checks", "ti-shield-check", "/hire-care-giver/background-checks"],
    ["Reports", "ti-chart-bar", "/hire-care-giver/reports"],
  ];
  const paymentPlanMenuItems = [
    ["Wallet", "ti-wallet", `/workspaces/${workspaceId}/payment-methods`],
    ["Charges", "ti-receipt", `/workspaces/${workspaceId}/charges`],
    ["Subscriptions", "ti-repeat", `/workspaces/${workspaceId}/subscriptions`],
    ["Partnerships", "ti-heart-handshake", "/partners"],
  ];
  const nav = [
    ["Dashboard", "ti-home", "/dashboard"],
    ["Residents", "ti-users", "/residents"],
    ["Assessments", "ti-clipboard-text", "/assessments"],
    ["Staff", "ti-users-group", "/dashboard"],
    ["Reports", "ti-chart-bar", "/dashboard"],
  ];
  const renderSubMenuItem = ([label, icon, path]) => (
    <Link className={`sidebar-subitem${location.pathname === path ? " active" : ""}`} to={path} key={label} title={label}>
      <i className={`ti ${icon}`} />
      <span>{label}</span>
    </Link>
  );

  return (
    <aside className={`sidebar${collapsed ? " is-collapsed" : ""}`}>
      <Logo />
      <nav>
        {nav.map(([label, icon, path]) => (
          <Link className={location.pathname === path || (path !== "/dashboard" && location.pathname.startsWith(path)) ? "active" : ""} to={path} key={label} title={label}>
            <i className={`ti ${icon}`} />
            <span className="sidebar-label">{label}</span>
          </Link>
        ))}
        <button type="button" className={`sidebar-group-toggle${hireOpen ? " is-open" : ""}`} onClick={() => setHireOpen((current) => !current)} title="Hire Care Giver" aria-expanded={hireOpen}>
          <i className="ti ti-user-search" />
          <span className="sidebar-label">Hire Care Giver</span>
          <i className="ti ti-chevron-down sidebar-chevron" />
        </button>
        {hireOpen && !collapsed && <div className="sidebar-submenu">{hireCareGiverMenuItems.map(renderSubMenuItem)}</div>}
        <button type="button" className={`sidebar-group-toggle${settingsOpen ? " is-open" : ""}`} onClick={() => setSettingsOpen((current) => !current)} title="Settings" aria-expanded={settingsOpen}>
          <i className="ti ti-settings" />
          <span className="sidebar-label">Settings</span>
          <i className="ti ti-chevron-down sidebar-chevron" />
        </button>
        {settingsOpen && !collapsed && <div className="sidebar-submenu">{generalMenuItems.map(renderSubMenuItem)}</div>}
        <button type="button" className={`sidebar-group-toggle${paymentsOpen ? " is-open" : ""}`} onClick={() => setPaymentsOpen((current) => !current)} title="Payments & Plans" aria-expanded={paymentsOpen}>
          <i className="ti ti-credit-card" />
          <span className="sidebar-label">Payments & Plans</span>
          <i className="ti ti-chevron-down sidebar-chevron" />
        </button>
        {paymentsOpen && !collapsed && <div className="sidebar-submenu">{paymentPlanMenuItems.map(renderSubMenuItem)}</div>}
      </nav>
      <div className="facility-card">
        <span className="soft-icon"><i className="ti ti-building-hospital" /></span>
        <div><strong>Sunset Gardens</strong><small>Assisted Living</small></div>
        <i className="ti ti-chevron-down" />
      </div>
      <div className="help-card">
        <strong>Need help?</strong>
        <p>Visit our Help Center or contact support.</p>
        <a href="#help">Help Center <i className="ti ti-arrow-right" /></a>
      </div>
    </aside>
  );
}

export function CareShell({ children, className = "dashboard-content" }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();

  return (
    <div className={`app-shell${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      <SideNav collapsed={sidebarCollapsed} />
      <main className="dashboard-main">
        <div className="dashboard-top">
          <div className="page-nav-actions">
            <button className="icon-button" aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"} onClick={() => setSidebarCollapsed((current) => !current)}>
              <i className="ti ti-menu-2" />
            </button>
            <button className="icon-button" aria-label="Go back" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left" />
            </button>
            <button className="icon-button" aria-label="Refresh page" onClick={() => window.location.reload()}>
              <i className="ti ti-refresh" />
            </button>
          </div>
          <div className="care-header-workspace">
            <WorkspacePicker />
          </div>
          <HeaderUserProfile />
        </div>
        <section className={className}>{children}</section>
      </main>
    </div>
  );
}

export function Panel({ title, action, actionNode, children }) {
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{title}</h2>
        {actionNode}
        {action && <a href="#view">{action}</a>}
      </div>
      {children}
    </section>
  );
}
